import { Routes } from '@angular/router';
import { authGuard } from './guards/auth.guard';
import { roleGuard } from './guards/role.guard';
import { UserRole } from './models/auth.model';

export const routes: Routes = [
  {
    path: 'login',
    loadComponent: () => import('./pages/login/login.component').then(m => m.LoginComponent)
  },
  {
    path: 'access-denied',
    loadComponent: () => import('./pages/access-denied/access-denied.component').then(m => m.AccessDeniedComponent)
  },
  {
    path: 'privacy-policy',
    loadComponent: () => import('./pages/privacy-policy/privacy-policy.component').then(m => m.PrivacyPolicyComponent)
  },
  {
    path: 'home',
    loadComponent: () => import('./pages/dashboard/dashboard.component').then(m => m.DashboardComponent),
    canActivate: [authGuard]
  },
  {
    path: 'all-restaurants',
    loadComponent: () => import('./pages/restaurants/restaurants.component').then(m => m.RestaurantsComponent),
    canActivate: [authGuard, roleGuard],
    data: { mode: 'all', roles: [UserRole.SuperAdmin] }
  },
  {
    path: 'restaurants',
    redirectTo: '/all-restaurants',
    pathMatch: 'full'
  },
  {
    path: 'my-restaurants',
    loadComponent: () => import('./pages/restaurants/restaurants.component').then(m => m.RestaurantsComponent),
    canActivate: [authGuard, roleGuard],
    data: { mode: 'my', roles: [UserRole.RestaurantOwner] }
  },
  {
    path: 'restaurant-menu-items',
    loadComponent: () => import('./pages/menu-items/menu-items').then(m => m.MenuItemsComponent),
    canActivate: [authGuard, roleGuard],
    data: { roles: [UserRole.SuperAdmin, UserRole.RestaurantOwner, UserRole.BranchManager] }
  },
  {
    path: 'restaurant-menu-items-prices',
    loadComponent: () => import('./pages/restaurant-menu-items-prices/restaurant-menu-items-prices').then(m => m.RestaurantMenuItemsPricesComponent),
    canActivate: [authGuard, roleGuard],
    data: { roles: [UserRole.SuperAdmin, UserRole.RestaurantOwner, UserRole.BranchManager] }
  },
  {
    path: 'categories',
    loadComponent: () => import('./pages/categories/categories').then(m => m.CategoriesComponent),
    canActivate: [authGuard, roleGuard],
    data: { roles: [UserRole.SuperAdmin, UserRole.RestaurantOwner] }
  },
  {
    path: 'branch-menu-items',
    loadComponent: () => import('./pages/branch-menu-items/branch-menu-items').then(m => m.BranchMenuItemsComponent),
    canActivate: [authGuard, roleGuard],
    data: { roles: [UserRole.SuperAdmin, UserRole.RestaurantOwner, UserRole.BranchManager] }
  },
  {
    path: 'all-branches',
    loadComponent: () => import('./pages/branches/branches.component').then(m => m.BranchesComponent),
    canActivate: [authGuard, roleGuard],
    data: { mode: 'all', roles: [UserRole.SuperAdmin] }
  },
  {
    path: 'my-branches',
    loadComponent: () => import('./pages/branches/branches.component').then(m => m.BranchesComponent),
    canActivate: [authGuard, roleGuard],
    data: { mode: 'my', roles: [UserRole.RestaurantOwner] }
  },
  {
    path: 'branches',
    redirectTo: '/all-branches',
    pathMatch: 'full'
  },
  {
    path: 'tables',
    loadComponent: () => import('./pages/tables/tables').then(m => m.TablesComponent),
    canActivate: [authGuard, roleGuard],
    data: { roles: [UserRole.SuperAdmin, UserRole.RestaurantOwner, UserRole.BranchManager] }
  },
  {
    path: 'restaurant-users',
    loadComponent: () => import('./pages/restaurant-users/restaurant-users.component').then(m => m.RestaurantUsersComponent),
    canActivate: [authGuard, roleGuard],
    data: { roles: [UserRole.SuperAdmin, UserRole.RestaurantOwner] }
  },
  {
    path: 'discounts',
    loadComponent: () => import('./pages/discounts/discounts.component').then(m => m.DiscountsComponent),
    canActivate: [authGuard, roleGuard],
    data: { roles: [UserRole.SuperAdmin, UserRole.RestaurantOwner] }
  },
  {
    path: 'reports/sales',
    loadComponent: () => import('./pages/reports/sales/sales-report.component').then(m => m.SalesReportComponent),
    canActivate: [authGuard, roleGuard],
    data: { roles: [UserRole.SuperAdmin, UserRole.RestaurantOwner, UserRole.BranchManager, UserRole.Cashier] }
  },
  {
    path: 'reports/branch-comparison',
    loadComponent: () => import('./pages/reports/branch-comparison/branch-comparison.component').then(m => m.BranchComparisonComponent),
    canActivate: [authGuard, roleGuard],
    data: { roles: [UserRole.SuperAdmin, UserRole.RestaurantOwner] }
  },
  {
    path: 'reports/table-utilization',
    loadComponent: () => import('./pages/reports/table-utilization/table-utilization.component').then(m => m.TableUtilizationComponent),
    canActivate: [authGuard, roleGuard],
    data: { roles: [UserRole.SuperAdmin, UserRole.RestaurantOwner, UserRole.BranchManager, UserRole.Cashier] }
  },
  {
    path: 'reports/cancellations',
    loadComponent: () => import('./pages/reports/cancellations/cancellations.component').then(m => m.CancellationsComponent),
    canActivate: [authGuard, roleGuard],
    data: { roles: [UserRole.SuperAdmin, UserRole.RestaurantOwner, UserRole.BranchManager, UserRole.Cashier] }
  },
  {
    path: 'reports/weekly-performance',
    loadComponent: () => import('./pages/reports/weekly-performance/weekly-performance.component').then(m => m.WeeklyPerformanceComponent),
    canActivate: [authGuard, roleGuard],
    data: { roles: [UserRole.SuperAdmin, UserRole.RestaurantOwner, UserRole.BranchManager, UserRole.Cashier] }
  },
  {
    path: 'reports/menu-item-trends',
    loadComponent: () => import('./pages/reports/menu-item-trends/menu-item-trends.component').then(m => m.MenuItemTrendsComponent),
    canActivate: [authGuard, roleGuard],
    data: { roles: [UserRole.SuperAdmin, UserRole.RestaurantOwner, UserRole.BranchManager, UserRole.Cashier] }
  },
  {
    path: 'reports/monthly-review',
    loadComponent: () => import('./pages/reports/monthly-review/monthly-review.component').then(m => m.MonthlyReviewComponent),
    canActivate: [authGuard, roleGuard],
    data: { roles: [UserRole.SuperAdmin, UserRole.RestaurantOwner, UserRole.BranchManager, UserRole.Cashier] }
  },
  {
    path: 'reports/custom-builder',
    loadComponent: () => import('./pages/reports/custom-builder/custom-builder.component').then(m => m.CustomBuilderComponent),
    canActivate: [authGuard, roleGuard],
    data: { roles: [UserRole.SuperAdmin, UserRole.RestaurantOwner, UserRole.BranchManager, UserRole.Cashier] }
  },
  // {
  //   path: 'reports/predictive-analytics',
  //   loadComponent: () => import('./pages/reports/predictive-analytics/predictive-analytics.component').then(m => m.PredictiveAnalyticsComponent),
  //   canActivate: [authGuard, roleGuard],
  //   data: { roles: [UserRole.SuperAdmin, UserRole.RestaurantOwner] }
  // },
  {
    path: 'reports/benchmarking',
    loadComponent: () => import('./pages/reports/benchmarking/benchmarking.component').then(m => m.BenchmarkingComponent),
    canActivate: [authGuard, roleGuard],
    data: { roles: [UserRole.SuperAdmin, UserRole.RestaurantOwner] }
  },
  {
    path: 'activity-types',
    loadComponent: () => import('./pages/activity-types/activity-types.component').then(m => m.ActivityTypesComponent),
    canActivate: [authGuard, roleGuard],
    data: { roles: [UserRole.SuperAdmin, UserRole.RestaurantOwner] }
  },
  {
    path: 'activities',
    loadComponent: () => import('./pages/activities/activities.component').then(m => m.ActivitiesComponent),
    canActivate: [authGuard, roleGuard],
    data: { roles: [UserRole.SuperAdmin, UserRole.RestaurantOwner] }
  },
  {
    path: 'activity-board',
    loadComponent: () => import('./pages/activity-board/activity-board.component').then(m => m.ActivityBoardComponent),
    canActivate: [authGuard, roleGuard],
    data: { roles: [UserRole.SuperAdmin, UserRole.RestaurantOwner, UserRole.BranchManager, UserRole.Cashier] }
  },
  {
    path: '',
    redirectTo: '/home',
    pathMatch: 'full'
  },
  {
    path: '**',
    redirectTo: '/home'
  }
];
